import { Modal } from "antd";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";


const SDIModal = ({
    children,
    draggable = true,
    resetOnClose = true,
    modalRender: userModalRender,
    ...modalProps
}) => {
    const containerRef = useRef(null);
    const dragDataRef = useRef({ startX: 0, startY: 0, dragging: false });
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const isOpen = useMemo(
        () => (modalProps.open ?? modalProps.visible ?? false),
        [modalProps.open, modalProps.visible]
    );

    const handleMouseMove = useCallback((event) => {
        if (!dragDataRef.current.dragging) {
            return;
        }
        setPosition({
            x: event.clientX - dragDataRef.current.startX,
            y: event.clientY - dragDataRef.current.startY,
        });
    }, []);

    const handleMouseUp = useCallback(() => {
        if (!dragDataRef.current.dragging) {
            return;
        }
        dragDataRef.current.dragging = false;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
    }, [handleMouseMove]);

    const handleTouchMove = useCallback((event) => {
        if (!dragDataRef.current.dragging || event.touches.length !== 1) {
            return;
        }
        const touch = event.touches[0];
        setPosition({
            x: touch.clientX - dragDataRef.current.startX,
            y: touch.clientY - dragDataRef.current.startY,
        });
        event.preventDefault();
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (!dragDataRef.current.dragging) {
            return;
        }
        dragDataRef.current.dragging = false;
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
        window.removeEventListener("touchcancel", handleTouchEnd);
    }, [handleTouchMove]);

    const shouldIgnoreDragTarget = useCallback((target) => {
        if (!target) {
            return true;
        }
        const closeNode = containerRef.current?.querySelector(".ant-modal-close");
        if (closeNode && closeNode.contains(target)) {
            return true;
        }
        if (target.closest && target.closest("input, textarea, button, select, [role='combobox'], [role='spinbutton'], [role='slider'], [contenteditable='true'], .ant-select, .ant-picker, .ant-input, .ant-btn")) {
            return true;
        }
        return false;
    }, []);

    const canStartDrag = useCallback((target) => {
        if (!target || !containerRef.current) {
            return false;
        }
        const headerNode = containerRef.current.querySelector(".ant-modal-header");
        const contentNode = containerRef.current.querySelector(".ant-modal-content");
        if (shouldIgnoreDragTarget(target)) {
            return false;
        }
        if (headerNode && headerNode.contains(target)) {
            return true;
        }
        if (contentNode && contentNode.contains(target)) {
            return true;
        }
        return false;
    }, [shouldIgnoreDragTarget]);

    const beginDrag = useCallback((clientX, clientY) => {
        dragDataRef.current = {
            startX: clientX - position.x,
            startY: clientY - position.y,
            dragging: true,
        };
    }, [position.x, position.y]);

    const handleMouseDown = useCallback((event) => {
        if (!draggable || event.button !== 0) {
            return;
        }
        if (!canStartDrag(event.target)) {
            return;
        }
        beginDrag(event.clientX, event.clientY);
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        event.preventDefault();
    }, [draggable, canStartDrag, beginDrag, handleMouseMove, handleMouseUp]);

    const handleTouchStart = useCallback((event) => {
        if (!draggable || event.touches.length !== 1) {
            return;
        }
        const touch = event.touches[0];
        if (!canStartDrag(event.target)) {
            return;
        }
        beginDrag(touch.clientX, touch.clientY);
        window.addEventListener("touchmove", handleTouchMove, { passive: false });
        window.addEventListener("touchend", handleTouchEnd);
        window.addEventListener("touchcancel", handleTouchEnd);
        event.preventDefault();
    }, [draggable, canStartDrag, beginDrag, handleTouchMove, handleTouchEnd]);

    useEffect(() => {
        if (isOpen) {
            if (resetOnClose) {
                setPosition({ x: 0, y: 0 });
            }
            dragDataRef.current.dragging = false;
            return;
        }
        dragDataRef.current.dragging = false;
        if (resetOnClose) {
            setPosition({ x: 0, y: 0 });
        }
    }, [isOpen, resetOnClose]);

    useEffect(() => {
        if (!draggable || !isOpen) {
            return;
        }
        const headerNode = containerRef.current?.querySelector(".ant-modal-header");
        if (headerNode) {
            headerNode.style.cursor = "move";
            headerNode.style.userSelect = "none";
            headerNode.style.touchAction = "none";
        }
    }, [draggable, isOpen]);

    useEffect(() => () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
        window.removeEventListener("touchcancel", handleTouchEnd);
    }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    const finalWidth = modalProps.width ?? "80vw";
    const finalBodyStyle = {
        padding: 24,
        maxHeight: "calc(100vh - 160px)",
        overflowY: "auto",
        ...(modalProps.bodyStyle || {}),
    };
    const { maskClosable, keyboard, style: providedStyle, wrapClassName, ...restModalProps } = modalProps;
    const finalStyle = {
        top: 40,
        ...(providedStyle || {}),
    };
    const finalWrapClassName = ["sdi-modal", wrapClassName].filter(Boolean).join(" ");

    return (
        <Modal
            {...restModalProps}
            maskClosable={maskClosable ?? false}
            keyboard={keyboard ?? false}
            width={finalWidth}
            style={finalStyle}
            // bodyStyle={finalBodyStyle}
            wrapClassName={finalWrapClassName}
            modalRender={(modalNode) => {
                const wrappedNode = (
                    <div
                        ref={containerRef}
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleTouchStart}
                        style={{
                            transform: `translate(${position.x}px, ${position.y}px)`,
                            transition: "none",
                        }}
                    >
                        {modalNode}
                    </div>
                );
                return typeof userModalRender === "function" ? userModalRender(wrappedNode) : wrappedNode;
            }}
            // closeIcon={<span className="text-secondary">x</span>}
        >
            {children}
        </Modal>
    );
};

SDIModal.propTypes = {
    children: PropTypes.node,
    draggable: PropTypes.bool,
    resetOnClose: PropTypes.bool,
    open: PropTypes.bool,
    visible: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bodyStyle: PropTypes.object,
    style: PropTypes.object,
    wrapClassName: PropTypes.string,
    modalRender: PropTypes.func,
};

export default SDIModal;
